(() => {
  const componentName = 'vl-native-scroll-carousel';

  const cssEaseProgress = (progress) => {
    const sample = (t, p1, p2) => ((1 - 3 * p2 + 3 * p1) * t + (3 * p2 - 6 * p1)) * t * t + 3 * p1 * t;
    const sampleDerivative = (t, p1, p2) => (3 * (1 - 3 * p2 + 3 * p1) * t + 2 * (3 * p2 - 6 * p1)) * t + 3 * p1;

    let parameter = progress;
    for (let iteration = 0; iteration < 5; iteration += 1) {
      const derivative = sampleDerivative(parameter, 0.25, 0.25);
      if (Math.abs(derivative) < 0.00001) break;
      parameter -= (sample(parameter, 0.25, 0.25) - progress) / derivative;
    }

    return sample(Math.min(1, Math.max(0, parameter)), 0.1, 1);
  };

  if (customElements.get(componentName)) return;

  class VLNativeScrollCarousel extends HTMLElement {
    constructor() {
      super();

      this.selectedIndex = 0;
      this.selectedPageIndex = 0;
      this.slideCount = 0;
      this.pageCount = 0;
      this.snapPoints = [];
      this.isScrollable = false;
      this.isInitialized = false;
      this.abortController = null;
      this.resizeObserver = null;
      this.resizeFrame = null;
      this.scrollFrame = null;
      this.scrollAnimationFrame = null;
      this.scrollAnimationPreviousSnapType = null;
      this.scrollSettleTimer = null;
      this.pendingReason = null;
      this.autoplayTimer = null;
      this.autoplayProgressFrame = null;
      this.autoplayObserver = null;
      this.lazyMediaObserver = null;
      this.autoplayMediaQuery = null;
      this.autoplayStartTime = 0;
      this.autoplayStoppedByInteraction = false;
      this.autoplayManuallyPaused = false;
      this.isAutoplayInView = false;
      this.isDocumentVisible = !document.hidden;
      this.loopStartClones = [];
      this.loopEndClones = [];
      this.loopStartClone = null;
      this.loopEndClone = null;
      this.scrollbarDrag = null;
      this.viewportDrag = null;
      this.nativeTouchPointerId = null;
      this.suppressViewportClick = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleControlKeydown = this.handleControlKeydown.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleWheel = this.handleWheel.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.settleFromPosition = this.settleFromPosition.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleAutoplayMediaChange = this.handleAutoplayMediaChange.bind(this);
      this.advanceAutoplay = this.advanceAutoplay.bind(this);
      this.handleScrollbarPointerDown = this.handleScrollbarPointerDown.bind(this);
      this.handleScrollbarPointerMove = this.handleScrollbarPointerMove.bind(this);
      this.handleScrollbarPointerUp = this.handleScrollbarPointerUp.bind(this);
      this.handleViewportPointerDown = this.handleViewportPointerDown.bind(this);
      this.handleViewportPointerMove = this.handleViewportPointerMove.bind(this);
      this.handleViewportPointerUp = this.handleViewportPointerUp.bind(this);
      this.handleViewportClick = this.handleViewportClick.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;
      if (this.dataset.initMedia && !window.matchMedia(this.dataset.initMedia).matches) return;
      if (!this.collectElements()) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.addEventListener('click', this.handleClick, { signal });
      this.addEventListener('keydown', this.handleControlKeydown, { signal });
      this.viewport.addEventListener('keydown', this.handleKeydown, { signal });
      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true, signal });
      if (this.dataset.wheelForceAxis === 'true') {
        this.viewport.addEventListener('wheel', this.handleWheel, { passive: false, signal });
      }
      if (this.scrollbar && this.scrollbarThumb) {
        this.scrollbar.addEventListener('pointerdown', this.handleScrollbarPointerDown, { signal });
        this.scrollbar.addEventListener('pointermove', this.handleScrollbarPointerMove, { signal });
        this.scrollbar.addEventListener('pointerup', this.handleScrollbarPointerUp, { signal });
        this.scrollbar.addEventListener('pointercancel', this.handleScrollbarPointerUp, { signal });
      }
      if (this.pointerDragEnabled) {
        this.viewport.addEventListener('pointerdown', this.handleViewportPointerDown, { signal });
        this.viewport.addEventListener('pointermove', this.handleViewportPointerMove, { signal });
        this.viewport.addEventListener('pointerup', this.handleViewportPointerUp, { signal });
        this.viewport.addEventListener('pointercancel', this.handleViewportPointerUp, { signal });
        this.viewport.addEventListener('click', this.handleViewportClick, { capture: true, signal });
      }
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.viewport);
        this.slides.forEach((slide) => this.resizeObserver.observe(slide));
      } else {
        window.addEventListener('resize', this.handleResize, { signal });
      }

      this.isInitialized = true;
      this.classList.add('is-initialized');
      this.refresh({ emit: false, preserveIndex: false });
      this.setupLazyMedia(signal);
      this.autoplayManuallyPaused = this.dataset.autoplayManual === 'true';
      this.setupAutoplay(signal);
      this.dispatchReady();
    }

    disconnectedCallback() {
      this.destroy();
    }

    collectElements() {
      this.viewport = this.querySelector('[data-vl-carousel-viewport]');
      this.track = this.querySelector('[data-vl-carousel-track]');
      this.setupLoopClones();
      this.slides = Array.from(this.querySelectorAll('[data-vl-carousel-slide]'))
        .filter((slide) => slide.dataset.vlCarouselClone !== 'true');
      this.pages = Array.from(this.querySelectorAll('[data-vl-carousel-page]'));
      this.progressElements = this.pages.map((page) => page.querySelector?.('[data-vl-carousel-progress]') || null);
      this.previousButton = this.querySelector('[data-vl-carousel-previous]');
      this.nextButton = this.querySelector('[data-vl-carousel-next]');
      this.scrollbar = this.querySelector('[data-vl-carousel-scrollbar]');
      this.scrollbarThumb = this.querySelector('[data-vl-carousel-scrollbar-thumb]');
      this.slideCount = this.slides.length;
      return Boolean(this.viewport && this.track && this.slideCount);
    }

    setupLoopClones() {
      if (!this.loop || !this.track || this.loopStartClones.length || this.loopEndClones.length) return;

      const originalSlides = Array.from(this.track.querySelectorAll('[data-vl-carousel-slide]'))
        .filter((slide) => slide.dataset.vlCarouselClone !== 'true');
      if (originalSlides.length < 2) return;

      const cloneCount = Math.min(this.loopCloneCount, originalSlides.length);
      const createClone = (slide, logicalIndex) => {
        const clone = slide.cloneNode(true);
        clone.dataset.vlCarouselClone = 'true';
        clone.dataset.vlCarouselLogicalIndex = String(logicalIndex);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');
        clone.querySelectorAll?.('[id]').forEach((element) => element.removeAttribute('id'));
        return clone;
      };

      const startIndex = originalSlides.length - cloneCount;
      this.loopStartClones = originalSlides
        .slice(startIndex)
        .map((slide, index) => createClone(slide, startIndex + index));
      this.loopEndClones = originalSlides
        .slice(0, cloneCount)
        .map((slide, index) => createClone(slide, index));

      [...this.loopStartClones].reverse().forEach((clone) => this.track.prepend(clone));
      this.loopEndClones.forEach((clone) => this.track.append(clone));
      this.loopStartClone = this.loopStartClones.at(-1) || null;
      this.loopEndClone = this.loopEndClones[0] || null;
    }

    setupLazyMedia(signal) {
      const lazySlides = this.slides.filter((slide) => slide.querySelector('[data-vl-carousel-lazy]'));
      if (!lazySlides.length) return;

      const loadSlide = (slide) => {
        slide.querySelectorAll('[data-vl-carousel-lazy]').forEach((media) => {
          if (media.classList.contains('is-loaded')) return;
          const source = media.dataset.src;
          if (!source) return;

          const reveal = () => {
            media.classList.add('is-loaded');
            requestAnimationFrame(() => this.refresh({ emit: false, preserveIndex: true }));
          };
          media.addEventListener('load', reveal, { once: true, signal });
          media.addEventListener('error', reveal, { once: true, signal });
          media.loading = 'eager';
          media.src = source;
          if (media.complete) reveal();
        });
      };

      if (!('IntersectionObserver' in window)) {
        lazySlides.forEach(loadSlide);
        return;
      }

      this.lazyMediaObserver = new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadSlide(entry.target);
          this.lazyMediaObserver?.unobserve(entry.target);
        });
      }, { root: this.viewport, threshold: 0.01 });
      lazySlides.forEach((slide) => this.lazyMediaObserver.observe(slide));
    }

    handleClick(event) {
      const previousButton = event.target.closest('[data-vl-carousel-previous]');
      if (previousButton && this.contains(previousButton)) {
        if (previousButton.disabled || previousButton.hasAttribute('disabled')) return;
        this.previous({ reason: 'previous', userInitiated: true });
        return;
      }

      const nextButton = event.target.closest('[data-vl-carousel-next]');
      if (nextButton && this.contains(nextButton)) {
        if (nextButton.disabled || nextButton.hasAttribute('disabled')) return;
        this.next({ reason: 'next', userInitiated: true });
        return;
      }

      const page = event.target.closest('[data-vl-carousel-page]');
      if (page && this.contains(page) && this.paginationInteractive) {
        this.goToPage(Number(page.dataset.index), {
          reason: 'pagination',
          userInitiated: true,
        });
        return;
      }

      if (!this.slideToClickedEnabled) return;
      const clickedSlide = event.target.closest('[data-vl-carousel-slide]');
      if (!clickedSlide || !this.contains(clickedSlide)) return;

      const logicalIndex = clickedSlide.dataset.vlCarouselClone === 'true'
        ? Number.parseInt(clickedSlide.dataset.vlCarouselLogicalIndex || '0', 10)
        : this.slides.indexOf(clickedSlide);
      if (logicalIndex < 0 || logicalIndex === this.selectedIndex) return;
      this.goTo(logicalIndex, {
        reason: 'clicked-slide',
        userInitiated: true,
      });
    }

    handleControlKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;

      const control = event.target.closest(
        '[data-vl-carousel-previous], [data-vl-carousel-next], [data-vl-carousel-page]',
      );
      if (!control || !this.contains(control) || control.disabled || control.hasAttribute('disabled')) return;

      event.preventDefault();
      control.click();
    }

    handleKeydown(event) {
      if (event.target !== this.viewport) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous({ reason: 'keyboard', userInitiated: true });
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next({ reason: 'keyboard', userInitiated: true });
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goToPage(0, { reason: 'keyboard', userInitiated: true });
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goToPage(this.pageCount - 1, { reason: 'keyboard', userInitiated: true });
      }
    }

    handleScroll() {
      this.pendingReason ||= 'drag';

      if (this.scrollbar && this.scrollbarThumb) {
        if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateScrollbar();
        });
      }

      window.clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = window.setTimeout(this.settleFromPosition, 96);
    }

    handleWheel(event) {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || event.deltaX === 0) return;

      event.preventDefault();
      this.pendingReason = 'wheel';
      this.viewport.scrollLeft += event.deltaX;
      this.handleScroll();
    }

    handleScrollbarPointerDown(event) {
      if (!this.isScrollable || event.button > 0) return;

      const trackRect = this.scrollbar.getBoundingClientRect();
      const thumbRect = this.scrollbarThumb.getBoundingClientRect();
      const isThumb = event.target.closest?.('[data-vl-carousel-scrollbar-thumb]') === this.scrollbarThumb;
      const grabOffset = isThumb ? event.clientX - thumbRect.left : thumbRect.width / 2;

      event.preventDefault();
      this.scrollbarDrag = { grabOffset, pointerId: event.pointerId };
      this.scrollbar.setPointerCapture?.(event.pointerId);
      this.seekFromScrollbar(event.clientX, trackRect);
    }

    handleScrollbarPointerMove(event) {
      if (!this.scrollbarDrag || event.pointerId !== this.scrollbarDrag.pointerId) return;
      event.preventDefault();
      this.seekFromScrollbar(event.clientX);
    }

    handleScrollbarPointerUp(event) {
      if (!this.scrollbarDrag || event.pointerId !== this.scrollbarDrag.pointerId) return;

      this.scrollbar.releasePointerCapture?.(event.pointerId);
      this.scrollbarDrag = null;
      this.settleToNearestSnap('scrollbar');
    }

    seekFromScrollbar(clientX, suppliedTrackRect) {
      const trackRect = suppliedTrackRect || this.scrollbar.getBoundingClientRect();
      const thumbRect = this.scrollbarThumb.getBoundingClientRect();
      const travel = Math.max(0, trackRect.width - thumbRect.width);
      const thumbLeft = Math.max(0, Math.min(clientX - trackRect.left - this.scrollbarDrag.grabOffset, travel));
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const progress = travel > 0 ? thumbLeft / travel : 0;

      this.pendingReason = 'scrollbar';
      this.viewport.scrollLeft = progress * maxScroll;
      this.updateScrollbar();
    }

    handleViewportPointerDown(event) {
      if (!this.isScrollable || event.button > 0) return;

      if (event.pointerType === 'touch') {
        this.nativeTouchPointerId = event.pointerId;
        this.cancelScrollAnimation();
        window.clearTimeout(this.scrollSettleTimer);
        this.scrollSettleTimer = null;
        this.stopAutoplay({ resetProgress: false });
        return;
      }

      if (event.target.closest?.('a, button, input, select, textarea, iframe, video, [contenteditable]')) return;

      this.viewportDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: this.viewport.scrollLeft,
        dragging: false,
      };
      this.viewport.setPointerCapture?.(event.pointerId);
    }

    handleViewportPointerMove(event) {
      if (!this.viewportDrag || event.pointerId !== this.viewportDrag.pointerId) return;

      const delta = event.clientX - this.viewportDrag.startX;
      if (!this.viewportDrag.dragging && Math.abs(delta) < this.dragThreshold) return;

      event.preventDefault();
      this.viewportDrag.dragging = true;
      this.classList.add('is-pointer-dragging');
      this.pendingReason = 'drag';
      this.viewport.scrollLeft = this.viewportDrag.startScrollLeft - delta;
      this.updateScrollbar();
    }

    handleViewportPointerUp(event) {
      if (event.pointerId === this.nativeTouchPointerId) {
        this.nativeTouchPointerId = null;
        this.syncAutoplay();
        return;
      }

      if (!this.viewportDrag || event.pointerId !== this.viewportDrag.pointerId) return;

      const wasDragging = this.viewportDrag.dragging;
      this.viewport.releasePointerCapture?.(event.pointerId);
      this.viewportDrag = null;
      this.classList.remove('is-pointer-dragging');

      if (!wasDragging) return;
      this.suppressViewportClick = true;
      this.settleToNearestSnap('drag');
    }

    handleViewportClick(event) {
      if (!this.suppressViewportClick) return;
      this.suppressViewportClick = false;
      event.preventDefault();
      event.stopImmediatePropagation?.();
    }

    settleToNearestSnap(reason) {
      const nextPageIndex = this.getNearestPageIndex(this.viewport.scrollLeft);
      this.pendingReason = null;
      this.goToPage(nextPageIndex, {
        reason,
        userInitiated: true,
      });
    }

    handleBlockSelect(event) {
      const selectedSlide = event.target.closest('[data-vl-carousel-slide]');
      if (!selectedSlide || !this.contains(selectedSlide)) return;

      this.goTo(this.slides.indexOf(selectedSlide), {
        behavior: 'instant',
        reason: 'block-select',
        userInitiated: false,
      });
    }

    handleResize() {
      if (this.resizeFrame !== null) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.refresh({ emit: false, preserveIndex: true });
      });
    }

    handleVisibilityChange() {
      this.isDocumentVisible = !document.hidden;
      this.syncAutoplay();
    }

    handleAutoplayMediaChange() {
      this.syncAutoplay();
    }

    previous(options = {}) {
      if (this.loop && this.selectedPageIndex <= 0) {
        this.goToLoopClone('previous', options);
        return;
      }
      this.goToPage(this.selectedPageIndex - 1, options);
    }

    next(options = {}) {
      if (this.loop && this.selectedPageIndex >= this.pageCount - 1) {
        this.goToLoopClone('next', options);
        return;
      }
      this.goToPage(this.selectedPageIndex + 1, options);
    }

    goToLoopClone(direction, options = {}) {
      if (!this.isInitialized || !this.loop) return;

      const isPrevious = direction === 'previous';
      const clone = isPrevious ? this.loopStartClone : this.loopEndClone;
      const nextPageIndex = isPrevious ? this.pageCount - 1 : 0;
      if (!clone) {
        this.goToPage(nextPageIndex, options);
        return;
      }

      const reason = options.reason || direction;
      const userInitiated = options.userInitiated === true;
      const behavior = options.behavior === 'instant' || this.prefersReducedMotion ? 'auto' : 'smooth';
      this.pendingReason = reason;
      this.pendingLoopJump = { pageIndex: nextPageIndex };
      this.scrollToPosition(this.getElementPosition(clone), behavior);
      this.setSelectedPageIndex(nextPageIndex, { emit: true, reason, userInitiated });
    }

    goTo(index, options = {}) {
      if (!this.isInitialized) return;

      const nextIndex = this.clampIndex(index);
      const targetPosition = this.getTargetPosition(nextIndex);
      const nextPageIndex = this.getNearestPageIndex(targetPosition);
      this.goToPage(nextPageIndex, options);
    }

    goToPage(pageIndex, options = {}) {
      if (!this.isInitialized || !this.pageCount) return;

      const nextPageIndex = this.clampPageIndex(pageIndex);
      const snapPoint = this.snapPoints[nextPageIndex];
      const behavior = options.behavior === 'instant' || this.prefersReducedMotion ? 'auto' : 'smooth';
      const reason = options.reason || 'api';
      const userInitiated = options.userInitiated === true;

      this.pendingReason = reason;
      this.scrollToPosition(snapPoint.position, behavior);
      this.setSelectedPageIndex(nextPageIndex, { emit: true, reason, userInitiated });
    }

    scrollToPosition(position, behavior) {
      this.cancelScrollAnimation();
      if (behavior === 'auto' || this.scrollDuration <= 0) {
        this.viewport.scrollTo({ left: position, behavior: 'auto' });
        return;
      }

      const startPosition = this.viewport.scrollLeft;
      const distance = position - startPosition;
      if (Math.abs(distance) <= 1) return;

      const previousSnapType = this.viewport.style.scrollSnapType;
      this.scrollAnimationPreviousSnapType = previousSnapType;
      this.viewport.style.scrollSnapType = 'none';
      let startTime;
      const step = (frameTime) => {
        const currentTime = typeof frameTime === 'number' ? frameTime : Date.now();
        if (startTime === undefined) startTime = currentTime;
        const progress = Math.min(Math.max((currentTime - startTime) / this.scrollDuration, 0), 1);
        const easedProgress = this.scrollEasing === 'ease'
          ? cssEaseProgress(progress)
          : 1 - Math.pow(1 - progress, 3);
        this.viewport.scrollLeft = startPosition + distance * easedProgress;
        this.updateScrollbar();
        if (progress < 1) {
          this.scrollAnimationFrame = requestAnimationFrame(step);
          return;
        }
        this.scrollAnimationFrame = null;
        this.viewport.scrollLeft = position;
        this.viewport.style.scrollSnapType = this.scrollAnimationPreviousSnapType ?? previousSnapType;
        this.scrollAnimationPreviousSnapType = null;
      };
      this.scrollAnimationFrame = requestAnimationFrame(step);
    }

    cancelScrollAnimation() {
      if (this.scrollAnimationFrame !== null) cancelAnimationFrame(this.scrollAnimationFrame);
      this.scrollAnimationFrame = null;
      if (this.viewport && this.viewport.style.scrollSnapType === 'none') {
        this.viewport.style.scrollSnapType = this.scrollAnimationPreviousSnapType || '';
      }
      this.scrollAnimationPreviousSnapType = null;
    }

    refresh(options = {}) {
      if (!this.viewport || !this.track) return;

      const previousIndex = this.selectedIndex;
      const preserveIndex = options.preserveIndex !== false;
      this.collectElements();

      this.updateScrollableState();
      this.calculateSnapPoints();
      const nextIndex = preserveIndex ? this.clampIndex(previousIndex) : 0;
      const nextPageIndex = this.getNearestPageIndex(this.getTargetPosition(nextIndex));
      const snapPoint = this.snapPoints[nextPageIndex];

      this.viewport.scrollTo({ left: snapPoint?.position || 0, behavior: 'auto' });
      this.setSelectedPageIndex(nextPageIndex, {
        emit: options.emit === true,
        reason: 'refresh',
        userInitiated: false,
      });
    }

    settleFromPosition() {
      this.scrollSettleTimer = null;
      if (this.pendingLoopJump) {
        const { pageIndex } = this.pendingLoopJump;
        this.pendingLoopJump = null;
        this.viewport.scrollTo({
          left: this.snapPoints[pageIndex]?.position || 0,
          behavior: 'auto',
        });
        this.pendingReason = null;
        return;
      }

      const loopPageIndex = this.getLoopPageIndexFromPosition(this.viewport.scrollLeft);
      if (loopPageIndex !== null) {
        const reason = this.pendingReason || 'drag';
        this.pendingReason = null;
        this.setSelectedPageIndex(loopPageIndex, {
          emit: true,
          reason,
          userInitiated: this.isUserInteractionReason(reason),
        });
        this.viewport.scrollTo({
          left: this.snapPoints[loopPageIndex]?.position || 0,
          behavior: 'auto',
        });
        return;
      }

      const nextPageIndex = this.getNearestPageIndex(this.viewport.scrollLeft);
      const reason = this.pendingReason || 'drag';
      this.pendingReason = null;
      this.setSelectedPageIndex(nextPageIndex, {
        emit: true,
        reason,
        userInitiated: this.isUserInteractionReason(reason),
      });
    }

    isUserInteractionReason(reason) {
      return ['drag', 'wheel', 'scrollbar'].includes(reason);
    }

    calculateSnapPoints() {
      const snapPoints = [];

      this.slides.forEach((slide, slideIndex) => {
        const position = this.getTargetPosition(slideIndex);
        const previousPoint = snapPoints[snapPoints.length - 1];
        if (
          !this.loop
          && previousPoint
          && Math.abs(previousPoint.position - position) <= this.snapMergeThreshold
        ) {
          if (this.dataset.snapMergeThreshold !== undefined) {
            previousPoint.position = position;
            previousPoint.slideIndex = slideIndex;
          }
          return;
        }
        snapPoints.push({ position, slideIndex });
      });

      this.snapPoints = snapPoints;
      this.pageCount = snapPoints.length;
    }

    getNearestPageIndex(position) {
      let nearestPageIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.snapPoints.forEach((snapPoint, pageIndex) => {
        const distance = Math.abs(snapPoint.position - position);
        if (distance >= nearestDistance) return;
        nearestDistance = distance;
        nearestPageIndex = pageIndex;
      });

      return nearestPageIndex;
    }

    getTargetPosition(index) {
      const slide = this.slides[this.clampIndex(index)];
      if (!slide) return 0;

      return this.getElementPosition(slide);
    }

    getElementPosition(element) {
      if (!element) return 0;

      const viewportRect = this.viewport.getBoundingClientRect();
      const slideRect = element.getBoundingClientRect();
      const alignCenter = this.dataset.align === 'center';
      const delta = alignCenter
        ? slideRect.left + slideRect.width / 2 - (viewportRect.left + viewportRect.width / 2)
        : slideRect.left - viewportRect.left - this.getScrollPadding();
      const maxPosition = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      return Math.max(0, Math.min(this.viewport.scrollLeft + delta, maxPosition));
    }

    getLoopPageIndexFromPosition(position) {
      if (!this.loop || !this.loopStartClones.length || !this.loopEndClones.length) return null;

      const nearestPageIndex = this.getNearestPageIndex(position);
      const nearestDistance = Math.abs((this.snapPoints[nearestPageIndex]?.position || 0) - position);
      let nearestClone = null;
      let nearestCloneDistance = Number.POSITIVE_INFINITY;

      [...this.loopStartClones, ...this.loopEndClones].forEach((clone) => {
        const distance = Math.abs(this.getElementPosition(clone) - position);
        if (distance >= nearestCloneDistance) return;
        nearestClone = clone;
        nearestCloneDistance = distance;
      });

      if (!nearestClone || nearestCloneDistance >= nearestDistance) return null;
      const logicalIndex = Number.parseInt(nearestClone.dataset.vlCarouselLogicalIndex || '0', 10);
      return this.getNearestPageIndex(this.getTargetPosition(logicalIndex));
    }

    getScrollPadding() {
      return Number.parseFloat(getComputedStyle(this.viewport).scrollPaddingLeft) || 0;
    }

    clampIndex(index) {
      const numericIndex = Number.isFinite(Number(index)) ? Number(index) : 0;
      return Math.max(0, Math.min(Math.round(numericIndex), Math.max(0, this.slideCount - 1)));
    }

    clampPageIndex(index) {
      const numericIndex = Number.isFinite(Number(index)) ? Number(index) : 0;
      return Math.max(0, Math.min(Math.round(numericIndex), Math.max(0, this.pageCount - 1)));
    }

    setSelectedPageIndex(pageIndex, options = {}) {
      const nextPageIndex = this.clampPageIndex(pageIndex);
      const nextIndex = this.snapPoints[nextPageIndex]?.slideIndex || 0;
      const previousIndex = this.selectedIndex;
      const previousPageIndex = this.selectedPageIndex;
      this.selectedIndex = nextIndex;
      this.selectedPageIndex = nextPageIndex;
      this.dataset.selectedIndex = String(nextIndex);
      this.dataset.selectedPageIndex = String(nextPageIndex);
      this.updateUI();

      if (options.userInitiated === true && !this.autoplayContinuesAfterInteraction) {
        this.autoplayStoppedByInteraction = true;
        this.stopAutoplay({ resetProgress: true });
      }

      if (nextPageIndex === previousPageIndex) return;

      this.restartAutoplayCycle();

      if (options.emit !== true) return;

      this.dispatchEvent(new CustomEvent('vl:carousel:change', {
        bubbles: true,
        detail: {
          index: nextIndex,
          previousIndex,
          pageIndex: nextPageIndex,
          previousPageIndex,
          slideCount: this.slideCount,
          pageCount: this.pageCount,
          reason: options.reason || 'api',
          userInitiated: options.userInitiated === true,
        },
      }));
    }

    setupAutoplay(signal) {
      if (!this.autoplayEnabled) return;

      this.isDocumentVisible = this.autoplayPauseOnHidden ? !document.hidden : true;
      if (this.autoplayPauseOnHidden) {
        document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });
      }

      if (this.dataset.autoplayMedia) {
        this.autoplayMediaQuery = window.matchMedia(this.dataset.autoplayMedia);
        if (typeof this.autoplayMediaQuery.addEventListener === 'function') {
          this.autoplayMediaQuery.addEventListener('change', this.handleAutoplayMediaChange, { signal });
        } else if (typeof this.autoplayMediaQuery.addListener === 'function') {
          this.autoplayMediaQuery.addListener(this.handleAutoplayMediaChange);
        }
      }

      if (this.autoplayInViewRequired && 'IntersectionObserver' in window) {
        this.autoplayObserver = new window.IntersectionObserver((entries) => {
          const entry = entries.find((candidate) => candidate.target === this) || entries[0];
          if (!entry) return;

          this.isAutoplayInView = entry.isIntersecting
            && entry.intersectionRatio >= this.autoplayInViewThreshold;
          this.syncAutoplay();
        }, {
          threshold: [0, this.autoplayInViewThreshold],
        });
        this.autoplayObserver.observe(this);
        return;
      }

      this.isAutoplayInView = true;
      this.syncAutoplay();
    }

    syncAutoplay() {
      if (!this.canAutoplay) {
        this.stopAutoplay({ resetProgress: true });
        return;
      }

      if (this.autoplayTimer === null && this.autoplayProgressFrame === null) {
        this.restartAutoplayCycle();
      }
    }

    restartAutoplayCycle() {
      this.stopAutoplay({ resetProgress: true });
      if (!this.canAutoplay) return;

      this.autoplayStartTime = this.now();
      this.autoplayTimer = window.setTimeout(this.advanceAutoplay, this.autoplayDelay);
      if (this.hasAutoplayProgress) {
        this.autoplayProgressFrame = requestAnimationFrame((currentTime) => {
          this.updateAutoplayProgressFrame(currentTime);
        });
      }
    }

    advanceAutoplay() {
      this.autoplayTimer = null;
      if (!this.canAutoplay) {
        this.stopAutoplay({ resetProgress: true });
        return;
      }

      if (this.loop) {
        this.next({
          reason: 'autoplay',
          userInitiated: false,
        });
        return;
      }

      const nextPageIndex = this.selectedPageIndex >= this.pageCount - 1 ? 0 : this.selectedPageIndex + 1;
      this.goToPage(nextPageIndex, {
        reason: 'autoplay',
        userInitiated: false,
      });
    }

    updateAutoplayProgressFrame(currentTime) {
      const elapsed = Math.max(0, currentTime - this.autoplayStartTime);
      const progress = Math.min(elapsed / this.autoplayDelay, 1);
      this.updateAutoplayProgress(progress);

      if (progress >= 1 || this.autoplayTimer === null) {
        this.autoplayProgressFrame = null;
        return;
      }

      this.autoplayProgressFrame = requestAnimationFrame((nextTime) => {
        this.updateAutoplayProgressFrame(nextTime);
      });
    }

    updateAutoplayProgress(progress) {
      this.progressElements.forEach((progressElement, index) => {
        if (!progressElement) return;

        const value = index === this.selectedPageIndex
          ? Math.min(Math.max(progress, 0), 1)
          : 0;
        progressElement.style.transform = `scaleX(${value})`;
      });
    }

    stopAutoplay(options = {}) {
      if (this.autoplayTimer !== null) window.clearTimeout(this.autoplayTimer);
      if (this.autoplayProgressFrame !== null) cancelAnimationFrame(this.autoplayProgressFrame);
      this.autoplayTimer = null;
      this.autoplayProgressFrame = null;

      if (options.resetProgress === true) this.updateAutoplayProgress(0);
    }

    pauseAutoplay() {
      this.autoplayManuallyPaused = true;
      this.stopAutoplay({ resetProgress: true });
    }

    resumeAutoplay() {
      this.autoplayManuallyPaused = false;
      this.syncAutoplay();
    }

    updateScrollableState() {
      this.isScrollable = this.viewport.scrollWidth > this.viewport.clientWidth + 1;
      this.classList.toggle('is-scrollable', this.isScrollable);
      this.classList.toggle('is-static', !this.isScrollable);
      this.updateScrollbar();
    }

    updateScrollbar() {
      if (!this.scrollbar || !this.scrollbarThumb) return;

      const trackWidth = this.scrollbar.getBoundingClientRect().width;
      const minimumWidth = Math.max(0, Number.parseFloat(this.scrollbar.dataset.minimumThumbWidth || '24') || 24);
      const visibleRatio = this.viewport.scrollWidth > 0
        ? Math.min(1, this.viewport.clientWidth / this.viewport.scrollWidth)
        : 1;
      const thumbWidth = Math.min(trackWidth, Math.max(minimumWidth, trackWidth * visibleRatio));
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const scrollProgress = maxScroll > 0 ? this.viewport.scrollLeft / maxScroll : 0;
      const travel = Math.max(0, trackWidth - thumbWidth);

      this.scrollbar.hidden = !this.isScrollable;
      this.scrollbarThumb.style.width = `${thumbWidth}px`;
      this.scrollbarThumb.style.transform = `translateX(${Math.max(0, Math.min(scrollProgress, 1)) * travel}px)`;
    }

    updateUI() {
      this.classList.toggle('is-single-page', this.pageCount <= 1);

      this.slides.forEach((slide, index) => {
        const isActive = index === this.selectedIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this.pages.forEach((page, index) => {
        const isAvailable = index < this.pageCount;
        const isActive = index === this.selectedPageIndex;
        page.hidden = !isAvailable;
        page.disabled = !isAvailable || !this.paginationInteractive;
        page.classList.toggle('is-active', isActive);
        if (isActive) page.setAttribute('aria-current', 'true');
        else page.removeAttribute('aria-current');
      });

      if (this.previousButton) {
        this.setControlDisabled(
          this.previousButton,
          !this.isScrollable || (!this.loop && this.selectedPageIndex <= 0),
        );
      }
      if (this.nextButton) {
        this.setControlDisabled(
          this.nextButton,
          !this.isScrollable || (!this.loop && this.selectedPageIndex >= this.pageCount - 1),
        );
      }
    }

    setControlDisabled(control, disabled) {
      control.disabled = disabled;
      control.toggleAttribute?.('disabled', disabled);
      control.tabIndex = disabled ? -1 : 0;
      control.classList.toggle('is-disabled', disabled);
      control.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    dispatchReady() {
      this.dispatchEvent(new CustomEvent('vl:carousel:ready', {
        bubbles: true,
        detail: {
          index: this.selectedIndex,
          slideCount: this.slideCount,
          pageIndex: this.selectedPageIndex,
          pageCount: this.pageCount,
        },
      }));
    }

    destroy() {
      if (!this.isInitialized) return;

      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.autoplayObserver?.disconnect();
      this.lazyMediaObserver?.disconnect();
      if (typeof this.autoplayMediaQuery?.removeListener === 'function') {
        this.autoplayMediaQuery.removeListener(this.handleAutoplayMediaChange);
      }
      if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
      if (this.resizeFrame !== null) cancelAnimationFrame(this.resizeFrame);
      this.cancelScrollAnimation();
      window.clearTimeout(this.scrollSettleTimer);
      this.stopAutoplay({ resetProgress: true });
      this.loopStartClones.forEach((clone) => clone.remove());
      this.loopEndClones.forEach((clone) => clone.remove());

      this.abortController = null;
      this.resizeObserver = null;
      this.resizeFrame = null;
      this.autoplayObserver = null;
      this.lazyMediaObserver = null;
      this.autoplayMediaQuery = null;
      this.loopStartClones = [];
      this.loopEndClones = [];
      this.loopStartClone = null;
      this.loopEndClone = null;
      this.scrollFrame = null;
      this.scrollAnimationFrame = null;
      this.scrollSettleTimer = null;
      this.pendingReason = null;
      this.scrollbarDrag = null;
      this.viewportDrag = null;
      this.nativeTouchPointerId = null;
      this.suppressViewportClick = false;
      this.autoplayStoppedByInteraction = false;
      this.autoplayManuallyPaused = false;
      this.isInitialized = false;
      this.classList.remove('is-initialized');
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    get paginationInteractive() {
      return this.dataset.paginationInteractive !== 'false';
    }

    get pointerDragEnabled() {
      return this.dataset.pointerDrag === 'true';
    }

    get slideToClickedEnabled() {
      return this.dataset.slideToClicked === 'true';
    }

    get dragThreshold() {
      const threshold = Number.parseFloat(this.dataset.dragThreshold || '0');
      return Math.max(0, Number.isFinite(threshold) ? threshold : 0);
    }

    get scrollDuration() {
      const duration = Number.parseFloat(this.dataset.scrollDuration || '300');
      return Math.max(0, Number.isFinite(duration) ? duration : 0);
    }

    get snapMergeThreshold() {
      const threshold = Number.parseFloat(this.dataset.snapMergeThreshold || '1');
      return Math.max(0, Number.isFinite(threshold) ? threshold : 1);
    }

    get scrollEasing() {
      return this.dataset.scrollEasing || 'cubic-out';
    }

    get autoplayDelay() {
      return Math.max(0, Number.parseInt(this.dataset.autoplayDelay || '0', 10) || 0);
    }

    get loop() {
      return this.dataset.loop === 'true';
    }

    get loopCloneCount() {
      return Math.max(1, Number.parseInt(this.dataset.loopCloneCount || '1', 10) || 1);
    }

    get autoplayEnabled() {
      return this.autoplayDelay > 0;
    }

    get hasAutoplayProgress() {
      return this.progressElements.some(Boolean);
    }

    get autoplayContinuesAfterInteraction() {
      return this.dataset.autoplayContinueOnInteraction !== 'false';
    }

    get autoplayInViewRequired() {
      return this.dataset.autoplayInView !== 'false';
    }

    get autoplayPauseOnHidden() {
      return this.dataset.autoplayPauseOnHidden !== 'false';
    }

    get autoplayInViewThreshold() {
      const threshold = Number.parseFloat(this.dataset.autoplayInViewThreshold || '0');
      return Math.min(Math.max(Number.isFinite(threshold) ? threshold : 0, 0), 1);
    }

    get canAutoplay() {
      return this.autoplayEnabled
        && this.isInitialized
        && !this.autoplayStoppedByInteraction
        && !this.autoplayManuallyPaused
        && this.nativeTouchPointerId === null
        && this.isAutoplayInView
        && this.isDocumentVisible
        && (!this.autoplayMediaQuery || this.autoplayMediaQuery.matches)
        && this.pageCount > 1;
    }

    now() {
      return window.performance?.now?.() || Date.now();
    }
  }

  customElements.define(componentName, VLNativeScrollCarousel);

  const fadeComponentName = 'vl-native-fade-carousel';

  if (customElements.get(fadeComponentName)) return;

  class VLNativeFadeCarousel extends HTMLElement {
    constructor() {
      super();
      this.selectedIndex = 0;
      this.isInitialized = false;
      this.autoplayTimer = null;
      this.autoplayStoppedByInteraction = false;
      this.pointerStart = null;
      this.suppressClick = false;
      this.abortController = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.advanceAutoplay = this.advanceAutoplay.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized || !this.collectElements()) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.addEventListener('click', this.handleClick, { signal });
      this.addEventListener('keydown', this.handleKeydown, { signal });
      this.viewport?.addEventListener('pointerdown', this.handlePointerDown, { signal });
      this.viewport?.addEventListener('pointerup', this.handlePointerUp, { signal });
      this.viewport?.addEventListener('pointercancel', this.handlePointerUp, { signal });
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
      document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });
      this.isInitialized = true;
      this.classList.add('is-initialized');
      this.select(0, { emit: false, reason: 'init' });
      this.restartAutoplay();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.stopAutoplay();
      this.abortController = null;
      this.autoplayStoppedByInteraction = false;
      this.pointerStart = null;
      this.suppressClick = false;
      this.isInitialized = false;
      this.classList.remove('is-initialized');
    }

    collectElements() {
      this.viewport = this.querySelector('[data-vl-fade-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-vl-fade-slide]'));
      this.pages = Array.from(this.querySelectorAll('[data-vl-fade-page]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-vl-fade-thumb]'));
      this.previousButton = this.querySelector('[data-vl-fade-previous]');
      this.nextButton = this.querySelector('[data-vl-fade-next]');
      return this.slides.length > 0;
    }

    refresh(options = {}) {
      const preserveIndex = options.preserveIndex !== false;
      const index = preserveIndex ? this.selectedIndex : 0;
      if (!this.collectElements()) return;
      this.select(index, { emit: options.emit === true, reason: 'refresh' });
    }

    handleClick(event) {
      if (this.suppressClick) {
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
        return;
      }

      const previous = event.target.closest('[data-vl-fade-previous]');
      if (previous && this.contains(previous)) {
        if (previous.disabled || previous.hasAttribute('disabled')) return;
        this.previous({ reason: 'previous', userInitiated: true });
        return;
      }

      const next = event.target.closest('[data-vl-fade-next]');
      if (next && this.contains(next)) {
        if (next.disabled || next.hasAttribute('disabled')) return;
        this.next({ reason: 'next', userInitiated: true });
        return;
      }

      const target = event.target.closest('[data-vl-fade-page], [data-vl-fade-thumb]');
      if (!target || !this.contains(target)) return;
      this.select(Number.parseInt(target.dataset.index || '0', 10), {
        reason: target.hasAttribute('data-vl-fade-thumb') ? 'thumb' : 'pagination',
        userInitiated: true,
      });
    }

    handleKeydown(event) {
      const isViewportNavigation = event.target === this.viewport;
      if (event.key === 'ArrowLeft' && isViewportNavigation) {
        event.preventDefault();
        this.previous({ reason: 'keyboard', userInitiated: true });
      } else if (event.key === 'ArrowRight' && isViewportNavigation) {
        event.preventDefault();
        this.next({ reason: 'keyboard', userInitiated: true });
      } else if (event.key === 'Home' && isViewportNavigation) {
        event.preventDefault();
        this.select(0, { reason: 'keyboard', userInitiated: true });
      } else if (event.key === 'End' && isViewportNavigation) {
        event.preventDefault();
        this.select(this.slides.length - 1, { reason: 'keyboard', userInitiated: true });
      } else if (event.key === 'Enter' || event.key === ' ') {
        const control = event.target.closest('[data-vl-fade-previous], [data-vl-fade-next], [data-vl-fade-page], [data-vl-fade-thumb]');
        if (!control || !this.contains(control) || control.disabled || control.hasAttribute('disabled')) return;
        event.preventDefault();
        control.click();
      }
    }

    handlePointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      this.pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }

    handlePointerUp(event) {
      if (!this.pointerStart || (event.pointerId !== undefined && event.pointerId !== this.pointerStart.id)) return;
      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      this.pointerStart = null;
      if (Math.abs(deltaX) < this.dragThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      this.suppressClick = true;
      if (deltaX < 0) this.next({ reason: 'drag', userInitiated: true });
      else this.previous({ reason: 'drag', userInitiated: true });
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      if (!blockId) return;
      const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId || slide.getAttribute('block-id') === blockId);
      if (index >= 0) this.select(index, { reason: 'editor', userInitiated: true });
    }

    handleVisibilityChange() {
      if (document.hidden) this.stopAutoplay();
      else this.restartAutoplay();
    }

    previous(options = {}) {
      this.select(this.selectedIndex - 1, options);
    }

    next(options = {}) {
      this.select(this.selectedIndex + 1, options);
    }

    select(index, options = {}) {
      const count = this.slides.length;
      if (!count) return;
      let nextIndex = Number.isFinite(index) ? Math.round(index) : 0;
      if (this.loop) nextIndex = (nextIndex % count + count) % count;
      else nextIndex = Math.max(0, Math.min(nextIndex, count - 1));
      const previousIndex = this.selectedIndex;
      this.selectedIndex = nextIndex;
      this.dataset.selectedIndex = String(nextIndex);

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.toggleAttribute('inert', !active);
      });
      [...this.pages, ...this.thumbs].forEach((control) => {
        const active = Number.parseInt(control.dataset.index || '0', 10) === nextIndex;
        control.classList.toggle('is-active', active);
        if (active) control.setAttribute('aria-current', 'true');
        else control.removeAttribute('aria-current');
      });
      this.scrollThumbIntoView(this.thumbs[nextIndex]);
      this.updateButtons();

      if (options.userInitiated === true && !this.autoplayContinuesAfterInteraction) {
        this.autoplayStoppedByInteraction = true;
      }
      this.restartAutoplay();
      if (options.emit === false || nextIndex === previousIndex) return;
      this.dispatchEvent(new CustomEvent('vl:fade-carousel:change', {
        bubbles: true,
        detail: {
          index: nextIndex,
          previousIndex,
          reason: options.reason || 'api',
          userInitiated: options.userInitiated === true,
        },
      }));
    }

    scrollThumbIntoView(thumb) {
      const track = thumb?.closest?.('.testimonial-native-thumbs-track, [data-vl-fade-thumbs]');
      if (!track || track.scrollWidth <= track.clientWidth + 1) return;

      const trackRect = track.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      let nextScrollLeft = track.scrollLeft;

      if (thumbRect.left < trackRect.left) {
        nextScrollLeft -= trackRect.left - thumbRect.left;
      } else if (thumbRect.right > trackRect.right) {
        nextScrollLeft += thumbRect.right - trackRect.right;
      } else {
        return;
      }

      track.scrollTo({ left: nextScrollLeft, behavior: 'auto' });
    }

    updateButtons() {
      const disablePrevious = !this.loop && this.selectedIndex <= 0;
      const disableNext = !this.loop && this.selectedIndex >= this.slides.length - 1;
      this.setButtonDisabled(this.previousButton, disablePrevious);
      this.setButtonDisabled(this.nextButton, disableNext);
    }

    setButtonDisabled(button, disabled) {
      if (!button) return;
      button.toggleAttribute('disabled', disabled);
      button.classList.toggle('is-disabled', disabled);
      button.tabIndex = disabled ? -1 : 0;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    restartAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayDelay || this.slides.length < 2 || document.hidden || this.autoplayStoppedByInteraction) return;
      this.autoplayTimer = window.setTimeout(this.advanceAutoplay, this.autoplayDelay);
    }

    advanceAutoplay() {
      this.autoplayTimer = null;
      const nextIndex = !this.loop && this.selectedIndex >= this.slides.length - 1
        ? 0
        : this.selectedIndex + 1;
      this.select(nextIndex, { reason: 'autoplay', userInitiated: false });
    }

    stopAutoplay() {
      if (this.autoplayTimer !== null) window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    get loop() {
      return this.dataset.loop !== 'false';
    }

    get autoplayDelay() {
      return Math.max(0, Number.parseInt(this.dataset.autoplayDelay || '0', 10) || 0);
    }

    get autoplayContinuesAfterInteraction() {
      return this.dataset.autoplayContinueOnInteraction !== 'false';
    }

    get dragThreshold() {
      return Math.max(1, Number.parseFloat(this.dataset.dragThreshold || '12') || 12);
    }

  }

  customElements.define(fadeComponentName, VLNativeFadeCarousel);
})();
